import { create } from "zustand";
import { devtools } from "zustand/middleware";

export interface VesselPermissions {
  granted: string[];
  denied: string[];
}

export interface Role {
  id: string;
  name: string;
  description?: string;
  isSystemRole: boolean;
}

interface PermissionsState {
  /** The user's role in the current organization */
  role: Role | null;
  /** Organization-level permissions (permission codes) */
  permissions: string[];
  /** Vessel-specific permission overrides. Key: Vessel ID */
  vesselPermissions: Record<string, VesselPermissions>;
  /** Whether permissions have been loaded from the API */
  isLoaded: boolean;

  // Actions
  setPermissions: (data: {
    role: Role | null;
    permissions: string[];
    vesselPermissions: Record<string, VesselPermissions>;
  }) => void;
  clearPermissions: () => void;
}

/**
 * Store for managing the current user's permissions.
 * Populated from the GET /me endpoint.
 */
export const usePermissionsStore = create<PermissionsState>()(
  devtools(
    (set) => ({
      role: null,
      permissions: [],
      vesselPermissions: {},
      isLoaded: false,

      setPermissions: (data) =>
        set({
          role: data.role,
          permissions: data.permissions,
          vesselPermissions: data.vesselPermissions,
          isLoaded: true,
        }),

      clearPermissions: () =>
        set({
          role: null,
          permissions: [],
          vesselPermissions: {},
          isLoaded: false,
        }),
    }),
    { name: "permissions" }
  )
);

/**
 * Check if the user has a specific permission.
 * Evaluates vessel-level overrides when vesselId is provided.
 *
 * Evaluation order:
 * 1. Vessel Deny (if vesselId provided) → return false
 * 2. Vessel Grant (if vesselId provided) → return true
 * 3. Org-level permission → return result
 */
export function hasPermission(
  state: PermissionsState,
  permission: string,
  vesselId?: string
): boolean {
  const normalizedPerm = permission.toLowerCase();

  // Check vessel-level overrides first
  if (vesselId && state.vesselPermissions[vesselId]) {
    const overrides = state.vesselPermissions[vesselId];

    // Deny takes priority
    if (overrides.denied.some((p) => p.toLowerCase() === normalizedPerm)) {
      return false;
    }

    // Check granted
    if (overrides.granted.some((p) => p.toLowerCase() === normalizedPerm)) {
      return true;
    }
  }

  // Fall back to org-level permissions
  return state.permissions.some((p) => p.toLowerCase() === normalizedPerm);
}
